import TransactionWelCard from "src/components/transactionWelCard/TransactionWelCard";
import styles from "./reportsContent.module.scss";
import Table from "rc-table";
import {
  Autocomplete,
  Button,
  ButtonGroup,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField
} from "@mui/material";
import CircularLoader from "src/components/UI/circularLoader/CircularLoader";
import { useAppDispatch, useAppSelector } from "../store";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { LIMIT, PAGE } from "src/components/constant/queryConstants";
import TableColumnFilter from "src/components/UI/tableColumnFIlter/TableColumnFilter";
import { setColumnFilter } from "../filtersSlice";
import { reportContentColumns } from "./reportsContentConfig";
import {
  downloadReportAction,
  fetchBoardViewReportsContentAction,
  fetchOrgListAction,
  fetchReportsContentAction
} from "./reportsContentSlice";
import { CORPORATE, EMPTY_STRING, INDIVIDUAL, REFERER, reportTypeMapping } from "src/components/constant/constant";
import { dateTimeFormat } from "src/utils/utils";
import { debounce } from "lodash";
import { orgList } from "../organisation/organisationSlice";
import { ReportsContentRequest } from "src/network/graphql/analyticsService";
import { OrganizationType } from "src/types/commonTypes";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";

const BoardView = lazy(() => import("./BoardView"));

const useReportFilters = (
  reportId: string | undefined,
  bucket: string[],
  stage: string[],
  orgType: OrganizationType | null,
  organisation: orgList | null
) => {
  return useMemo(() => {
    const filter: any = {
      report_content_filter: {
        report_ids: [reportId]
      }
    };

    if (bucket.length > 0) filter.report_content_filter.buckets = bucket;
    if (stage.length > 0) filter.report_content_filter.stages = stage;
    if (orgType !== null) filter.report_content_filter.organisation_types = [orgType];
    if (organisation?.id) filter.report_content_filter.object_ids = [organisation.id];

    return filter;
  }, [reportId, bucket, stage, orgType, organisation]);
};

export default function ReportsContent() {
  const dispatch = useAppDispatch();
  const { reportId } = useParams();
  const { reportsContent, loading, orgLists, downloadReportLoading, boardViewReportsContent } = useAppSelector(
    (state) => state.reportsContent
  );
  const { column: columnFilter } = useAppSelector((state) => state.filters);
  const [searchParams, setSearchParams] = useSearchParams();
  const [bucket, setBucket] = useState<string[]>([]);
  const [stage, setStage] = useState<string[]>([]);
  const [boardView, setBoardView] = useState<boolean>(false);
  const [orgType, setOrgType] = useState<OrganizationType | null>(null);
  const [organisation, setOrganisation] = useState<orgList | null>(null);
  const limit = searchParams.get(LIMIT) ?? "10";
  const page = searchParams.get(PAGE) ?? "0";

  const filter = useReportFilters(reportId, bucket, stage, orgType, organisation);

  const resetPagination = async () => {
    searchParams.delete(PAGE);
    searchParams.delete(LIMIT);
    setSearchParams(searchParams);
  };

  useEffect(() => {
    resetPagination();
  }, [filter]);

  const fetchNovattiTransaction = useCallback(() => {
    if (reportId) {
      const data = {
        size: Number(limit),
        from: Number(limit) * Number(page),
        filter
      };

      dispatch(fetchReportsContentAction(data));
    }
  }, [reportId, limit, page, filter, dispatch]);

  const fetchBoardViewData = useCallback(() => {
    if (reportId && boardView) {
      const stages = ["STAGE_1", "STAGE_2", "STAGE_3", "STAGE_4", "STAGE_5"];

      stages.forEach((stage) => {
        const stageFilter = { ...filter, report_content_filter: { ...filter.report_content_filter, stages: [stage] } };
        const data = {
          size: 5,
          from: 0,
          filter: stageFilter
        };

        dispatch(fetchBoardViewReportsContentAction(data, true));
      });
    }
  }, [reportId, boardView, filter, dispatch]);

  useEffect(() => {
    if (boardView) {
      fetchBoardViewData();
    } else {
      fetchNovattiTransaction();
    }
  }, [fetchBoardViewData, fetchNovattiTransaction, boardView]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    searchParams.set(PAGE, String(newPage));
    setSearchParams(searchParams);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<{ value: unknown }>) => {
    searchParams.set(LIMIT, event.target.value as string);
    searchParams.delete(PAGE);
    setSearchParams(searchParams);
  };

  const filteredColumns = useMemo(() => {
    return reportContentColumns.filter((col) => columnFilter?.reportsContent?.includes(col.key));
  }, [columnFilter?.reportsContent]);

  const handleColumnSelection = (selectedColumns: string[]) => {
    dispatch(setColumnFilter({ reportsContent: selectedColumns }));
  };

  const handleSearchOrganisation = debounce((event: React.ChangeEvent<object>, input: string) => {
    if (input.length < 2) return;
    const data = {
      searchName: input,
      limit: 10,
      from: 0
    };

    dispatch(fetchOrgListAction(data));
  }, 500);

  const downloadReportContent = async () => {
    if (reportId) {
      const downloadData: ReportsContentRequest = {
        size: 500,
        from: 0,
        filter
      };

      dispatch(downloadReportAction(downloadData));
    }
  };

  return (
    <div className={styles.fileWrapper}>
      <div className={styles.WelCard}>
        <TransactionWelCard heading={"View Your Reports Content"} subHeading={"View details of Reports Content"} />
      </div>
      <div className={styles.fileList}>
        <div className={styles.fileListHead}>
          <div className={styles.fileListHeadIn}>
            <div className={styles.fileListHeadD}>
              <h3>{`Report Content List | ${reportTypeMapping[reportsContent?.list[0]?.report_type] || reportsContent?.list[0]?.report_type} | ${dateTimeFormat(reportsContent?.list[0]?.report_date)?.date}`}</h3>
              <p>Manage all the reports content</p>
            </div>
            <Button
              variant="contained"
              onClick={downloadReportContent}
              className={styles.downloadReport}
              disabled={downloadReportLoading}
              aria-label="Download Report"
            >
              {downloadReportLoading ? (
                <div className={styles.progress}>
                  <CircularProgress size={24} style={{ color: "white" }} />
                </div>
              ) : (
                "Download Report"
              )}
            </Button>
          </div>
          <ButtonGroup variant="outlined" aria-label="outlined button group">
            <Button
              startIcon={<ListOutlinedIcon />}
              style={{
                color: boardView ? "#0f172ae8" : "white",
                backgroundColor: boardView ? EMPTY_STRING : "#0f172ae8",
                borderColor: "#0f172ae8",
                textTransform: "none"
              }}
              onClick={() => setBoardView(false)}
            >
              List View
            </Button>
            <Button
              startIcon={<TableChartOutlinedIcon />}
              style={{
                color: boardView ? "white" : "#0f172ae8",
                backgroundColor: boardView ? "#0f172ae8" : EMPTY_STRING,
                borderColor: "#0f172ae8",
                textTransform: "none"
              }}
              onClick={() => setBoardView(true)}
            >
              Board View
            </Button>
          </ButtonGroup>
          <div className={styles.fileListFilter}>
            <div className={styles.selectFilter}>
              <FormControl sx={{ minWidth: 180, maxWidth: 300 }}>
                <InputLabel id="bucket-label">Bucket Name</InputLabel>
                <Select
                  multiple
                  value={bucket || []}
                  onChange={(e) => setBucket(e.target.value as string[])}
                  inputProps={{ "aria-label": "Without label" }}
                  labelId="bucket-label"
                  label="Bucket Name"
                >
                  <MenuItem value="ORGANISATION_REGISTERED">Organisation Registered</MenuItem>
                  <MenuItem value="SUBSCRIPTION_SELECTED">Subscription Selected</MenuItem>
                  <MenuItem value="USERS_CREATED">Users Created</MenuItem>
                  <MenuItem value="WALLETS_CREATED">Wallets Created</MenuItem>
                  <MenuItem value="PRE_TRANSACTION">Pre Transaction</MenuItem>
                  <MenuItem value="TRANSACTED">Transacted</MenuItem>
                </Select>
              </FormControl>
              {!boardView && (
                <FormControl sx={{ minWidth: 180, maxWidth: 300 }}>
                  <InputLabel id="stage-label">Stage</InputLabel>
                  <Select
                    multiple
                    value={stage}
                    onChange={(e) => setStage(e.target.value as string[])}
                    inputProps={{ "aria-label": "Without label" }}
                    labelId="stage-label"
                    label="Stage"
                  >
                    <MenuItem value="STAGE_1">Stage 1</MenuItem>
                    <MenuItem value="STAGE_2">Stage 2</MenuItem>
                    <MenuItem value="STAGE_3">Stage 3</MenuItem>
                    <MenuItem value="STAGE_4">Stage 4</MenuItem>
                    <MenuItem value="STAGE_5">Stage 5</MenuItem>
                  </Select>
                </FormControl>
              )}
              <FormControl sx={{ minWidth: 180, maxWidth: 300 }}>
                <InputLabel id="type-label">Type</InputLabel>
                <Select
                  value={orgType || EMPTY_STRING}
                  inputProps={{ "aria-label": "Without label" }}
                  labelId="type-label"
                  label="Type"
                  onChange={(e) =>
                    setOrgType(e.target.value === EMPTY_STRING ? null : (e.target.value as OrganizationType))
                  }
                >
                  <MenuItem value={EMPTY_STRING} sx={{ color: "grey" }}>
                    None
                  </MenuItem>
                  <MenuItem value={CORPORATE}>Corporate</MenuItem>
                  <MenuItem value={INDIVIDUAL}>Individual</MenuItem>
                  <MenuItem value={REFERER}>Referer</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200, maxWidth: 300 }}>
                <Autocomplete
                  sx={{
                    width: "100%",
                    ".MuiOutlinedInput-root": {
                      paddingRight: "9px !important"
                    }
                  }}
                  options={orgLists?.list}
                  getOptionLabel={(option) => option?.name}
                  filterOptions={(x) => x}
                  value={organisation}
                  onInputChange={handleSearchOrganisation}
                  onChange={(event, newValue) => setOrganisation(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: null
                      }}
                      label="Search Organisation"
                      placeholder="type to search...."
                    />
                  )}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start"
                      }}
                    >
                      <span>{option?.name}</span>
                      <span style={{ color: "#64748B", fontSize: "12px" }}>{option?.id}</span>
                    </li>
                  )}
                />
              </FormControl>
            </div>
            {!boardView && (
              <TableColumnFilter
                columns={reportContentColumns}
                selectedColumn={columnFilter?.reportsContent}
                mandatoryColumns={["object_id", "data"]}
                onApply={handleColumnSelection}
                iconContanerClass={styles.tableColumnContainer}
              />
            )}
          </div>
        </div>
        {boardView ? (
          <Suspense fallback={<CircularLoader />}>
            <BoardView
              boardViewData={boardViewReportsContent}
              fetchMoreData={(s: string) => {
                const stageFilter = {
                  ...filter,
                  report_content_filter: { ...filter.report_content_filter, stages: [s] }
                };
                const data = {
                  size: 5,
                  from: boardViewReportsContent[s].page * 5 || 0,
                  filter: stageFilter
                };

                dispatch(fetchBoardViewReportsContentAction(data));
              }}
            />
          </Suspense>
        ) : (
          <>
            <div className={styles.fileListTable}>
              <Table
                columns={filteredColumns}
                data={loading ? [] : reportsContent.list}
                className={styles.tableIn}
                rowKey="id"
                components={
                  loading
                    ? {
                        body: {
                          cell: CircularLoader
                        }
                      }
                    : undefined
                }
              />
            </div>
            <div className={styles.paginationWrapper}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={reportsContent.size ?? 0}
                page={parseInt(page)}
                onPageChange={handleChangePage}
                rowsPerPage={parseInt(limit)}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
